import apiClient from './apiClient';
import type { AiPrompt, AnalysisLimits, PromptLocale } from '../types/projectAnalysis';

const CONFIG_URL = '/api/configuration';

const orgParams = (organizationId?: string | null) => (organizationId ? { organization_id: organizationId } : {});

/** Configurable project prompts (per org and language) and analysis limits. */
export const aiPromptService = {
  /** Org admins read their org's; a super admin may pass any organization. */
  list: async (organizationId?: string | null): Promise<AiPrompt[]> => {
    const { data } = await apiClient.get<AiPrompt[]>(`${CONFIG_URL}/ai-prompts`, {
      params: orgParams(organizationId),
    });
    return data;
  },

  /** 400 with a readable detail when the text uses an unknown placeholder. */
  save: async (
    key: string,
    locale: PromptLocale,
    content: string,
    organizationId?: string | null
  ): Promise<AiPrompt> => {
    const { data } = await apiClient.put<AiPrompt>(
      `${CONFIG_URL}/ai-prompts/${encodeURIComponent(key)}`,
      { content },
      { params: { locale, ...orgParams(organizationId) } }
    );
    return data;
  },

  /** Back to the code default. */
  reset: async (key: string, locale: PromptLocale, organizationId?: string | null): Promise<AiPrompt> => {
    const { data } = await apiClient.delete<AiPrompt>(`${CONFIG_URL}/ai-prompts/${encodeURIComponent(key)}`, {
      params: { locale, ...orgParams(organizationId) },
    });
    return data;
  },

  /** Defaults, bounds, global and org values and the effective limits. */
  limits: async (organizationId?: string | null): Promise<AnalysisLimits> => {
    const { data } = await apiClient.get<AnalysisLimits>(`${CONFIG_URL}/project-analysis-limits`, {
      params: orgParams(organizationId),
    });
    return data;
  },

  /** Global limits (super admin); null resets every limit to the code defaults. */
  saveGlobalLimits: async (limits: Record<string, number> | null): Promise<AnalysisLimits> => {
    const { data } = await apiClient.put<AnalysisLimits>(`${CONFIG_URL}/project-analysis-limits`, { limits });
    return data;
  },
};

/**
 * Readable message from an API error whose detail may be a string, a
 * `{field: message}` map (limits out of bounds) or a pydantic error list.
 */
export function describeApiError(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
      .filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }
  if (detail && typeof detail === 'object') {
    const parts = Object.entries(detail as Record<string, unknown>).map(([k, v]) => `${k}: ${String(v)}`);
    if (parts.length) return parts.join(' · ');
  }
  return fallback;
}

export default aiPromptService;
